import { useEffect, useMemo, useState } from 'react';
import {
  CreditCard, Check, ChevronLeft, ChevronRight, AlertCircle, CheckCircle2,
  Package, Cpu, Globe, Shield, Mail, Wrench, Layers, Minus, Plus, Sparkles,
} from 'lucide-react';
import Card from '../../components/Card';
import Button from '../../components/Button';
import LoadingSpinner from '../../components/LoadingSpinner';
import { billingApi, type Plan, type BillingAddon, type BillingOrder } from '../../api/billing';
import { useCart } from '../../components/CartWidget';

const CAT_META: Record<string, { label: string; icon: any; blurb: string }> = {
  memory: { label: 'AI Memory', icon: Cpu, blurb: 'Give your assistant more long-term memory.' },
  domain: { label: 'Domain Names', icon: Globe, blurb: 'Register a domain for your project or brand.' },
  ssl: { label: 'SSL Certificates', icon: Shield, blurb: 'Secure any domain with HTTPS.' },
  email: { label: 'Business Email', icon: Mail, blurb: 'Professional mailboxes on your own domain.' },
  security: { label: 'Security & Backup', icon: Shield, blurb: 'Protect and back up your sites.' },
  hosting: { label: 'Hosting', icon: Layers, blurb: 'Hosting for your projects.' },
  other: { label: 'Other Add-ons', icon: Wrench, blurb: 'More ways to extend your plan.' },
};

function money(cents: number): string {
  return `$${(cents / 100).toFixed(2)}`;
}

const STEPS = ['Plan', 'Add-ons', 'Review'] as const;
type Step = 0 | 1 | 2;

interface Sel { quantity: number; domain_name?: string }

interface MultiStepBillingProps {
  /** Plans supplied by the parent (so the page loads them once). */
  plans: Plan[];
  /** Optional callback after an order is created. */
  onOrderCreated?: (order: BillingOrder) => void;
}

/**
 * MultiStepBilling — the multi-layer selection flow for plans + add-ons.
 * Step 1: choose a plan. Step 2: layer add-ons. Step 3: review & create order.
 */
export default function MultiStepBilling({ plans, onOrderCreated }: MultiStepBillingProps) {
  const { add: addToCart } = useCart();
  const [step, setStep] = useState<Step>(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [planKey, setPlanKey] = useState<string | null>(null);
  const [interval, setInterval] = useState<'monthly' | 'yearly'>('monthly');
  const [activeCat, setActiveCat] = useState<string>('');
  const [catalog, setCatalog] = useState<{ addons: BillingAddon[]; categories: { key: string }[]; by_category: Record<string, BillingAddon[]> } | null>(null);
  const [selection, setSelection] = useState<Record<string, Sel>>({});

  const [placing, setPlacing] = useState(false);
  const [order, setOrder] = useState<BillingOrder | null>(null);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const cat = await billingApi.getAddons();
        setCatalog(cat);
        const cats = cat.categories.map((c: { key: string }) => c.key);
        if (cats.length) setActiveCat(cats[0]);
      } catch {
        setError('Could not load add-ons.');
      } finally { setLoading(false); }
    })();
  }, []);

  useEffect(() => {
    const paid = plans.filter(p => p.billing_interval === interval);
    const firstPaid = paid.length ? paid[0].plan_key : (plans[0]?.plan_key ?? null);
    setPlanKey(prev => prev && plans.some(p => p.plan_key === prev) ? prev : firstPaid);
  }, [plans, interval]);

  const selectedPlan = useMemo(() => plans.find(p => p.plan_key === planKey) || null, [plans, planKey]);

  const intervals = useMemo(() => {
    const set = new Set(plans.map(p => p.billing_interval));
    return Array.from(set);
  }, [plans]);

  const plansForInterval = useMemo(
    () => plans.filter(p => p.billing_interval === interval || p.price_cents === 0),
    [plans, interval]);

  const addons = catalog?.addons ?? [];
  const cats = catalog?.categories ?? [];
  // A term toggle that filters add-ons by their own interval (monthly vs yearly)
  const [addonInterval, setAddonInterval] = useState<'all' | 'monthly' | 'yearly'>('all');
  const visibleAddons = useMemo(() => {
    let list = activeCat ? (catalog?.by_category[activeCat] ?? []) : addons;
    if (addonInterval !== 'all') list = list.filter(a => a.billing_interval === addonInterval);
    return list;
  }, [catalog, activeCat, addons, addonInterval]);

  const selList = useMemo(() => Object.entries(selection).filter(([, v]) => v.quantity > 0), [selection]);

  const itemsTotal = useMemo(() => selList.reduce((sum, [k, v]) => {
    const a = addons.find(x => x.addon_key === k);
    return sum + (a ? a.price_cents * v.quantity : 0);
  }, 0), [selList, addons]);

  const planTotal = selectedPlan ? selectedPlan.price_cents : 0;
  const total = planTotal + itemsTotal;

  const setQty = (key: string, qty: number, max: number | null) => {
    const clamped = Math.max(0, max ? Math.min(qty, max) : qty);
    setSelection(prev => ({ ...prev, [key]: { ...(prev[key] || {}), quantity: clamped } }));
  };

  const addSelectionToCart = async () => {
    const toAdd = selList.filter(([, v]) => v.quantity > 0);
    if (!toAdd.length) { setError('Pick at least one add-on first.'); return; }
    setError('');
    try {
      for (const [k, v] of toAdd) {
        await addToCart({ addon_key: k, quantity: v.quantity, domain_name: v.domain_name || null, plan_key: planKey });
      }
      setSuccess('Added to your cart.');
    } catch (err: any) {
      setError(err?.response?.data?.detail || 'Could not add to cart.');
    }
  };

  const placeOrder = async () => {
    setPlacing(true); setError(''); setSuccess('');
    try {
      const created = await billingApi.createOrder({
        plan_key: planKey,
        interval,
        items: selList.map(([k, v]) => ({ addon_key: k, quantity: v.quantity, domain_name: v.domain_name || null })),
      });
      setOrder(created);
      setSuccess('Order created. Continue to secure checkout to pay.');
      onOrderCreated?.(created);
    } catch (err: any) {
      setError(err?.response?.data?.detail || 'Could not create the order.');
    } finally { setPlacing(false); }
  };

  if (loading) return <div className="flex h-40 items-center justify-center"><LoadingSpinner size="lg" /></div>;

  return (
    <div className="space-y-4">
      {/* Step indicator */}
      <div className="flex items-center gap-2" data-agent-region="steps">
        {STEPS.map((label, i) => {
          const done = i < step;
          const active = i === step;
          return (
            <div key={label} className="flex min-w-0 flex-1 items-center gap-2">
              <button
                onClick={() => setStep(i as Step)}
                data-agent-id={`billing.step.${label.toLowerCase()}`}
                className={`flex items-center gap-2 rounded-full px-3 py-1.5 text-sm font-medium transition-colors ${
                  active ? 'bg-brand-500 text-white'
                    : done ? 'bg-green-500/10 text-green-600 dark:text-green-400'
                      : 'bg-slate-100 text-slate-500 dark:bg-slate-800 dark:text-slate-400'}`}>
                <span className="flex h-5 w-5 items-center justify-center rounded-full bg-white/20 text-xs">
                  {done ? <Check size={12} /> : i + 1}
                </span>
                <span className="truncate">{label}</span>
              </button>
              {i < STEPS.length - 1 && <div className="h-px min-w-2 flex-1 bg-slate-200 dark:bg-slate-700" />}
            </div>
          );
        })}
      </div>

      {error && <div className="flex items-center gap-2 rounded-lg border border-red-300 bg-red-50 px-3 py-2.5 text-sm text-red-700 dark:border-red-800 dark:bg-red-900/30 dark:text-red-400"><AlertCircle size={16} />{error}</div>}
      {success && <div className="flex items-center gap-2 rounded-lg border border-green-300 bg-green-50 px-3 py-2.5 text-sm text-green-700 dark:border-green-800 dark:bg-green-900/30 dark:text-green-400"><CheckCircle2 size={16} />{success}</div>}

      {/* ── STEP 1: PLAN ─────────────────────────────────────── */}
      {step === 0 && (
        <div className="space-y-4">
          <Card className="p-5">
            <div className="mb-3 flex items-center gap-2">
              <Package size={18} className="text-slate-500" />
              <h2 className="text-base font-semibold text-slate-950 dark:text-slate-100">Choose your plan</h2>
            </div>
            {intervals.length > 1 && (
              <div className="mb-4 inline-flex rounded-lg bg-slate-100 p-1 dark:bg-slate-800">
                {intervals.map(iv => (
                  <button key={iv} onClick={() => setInterval(iv as any)}
                    className={`rounded-md px-3 py-1.5 text-sm font-medium capitalize transition ${interval === iv ? 'bg-white text-slate-900 shadow-sm dark:bg-slate-700 dark:text-white' : 'text-slate-500'}`}>
                    {iv}
                  </button>
                ))}
              </div>
            )}
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {plansForInterval.map(p => {
                const sel = planKey === p.plan_key;
                return (
                  <button key={p.plan_key} onClick={() => setPlanKey(p.plan_key)}
                    data-agent-id={`billing.plan.${p.plan_key}`}
                    className={`rounded-xl border p-4 text-left transition ${sel ? 'border-brand-500 bg-brand-500/5 ring-1 ring-brand-500' : 'border-slate-200 hover:border-slate-300 dark:border-slate-700 dark:hover:border-slate-600'}`}>
                    <div className="flex items-start justify-between">
                      <span className="font-medium text-slate-950 dark:text-slate-100">{p.display_name}</span>
                      {sel && <Check size={16} className="text-brand-500" />}
                    </div>
                    {p.description && <p className="mt-1 line-clamp-2 text-xs text-slate-500">{p.description}</p>}
                    <div className="mt-3 text-xl font-bold text-slate-950 dark:text-slate-100">
                      {p.price_cents === 0 ? 'Free' : money(p.price_cents)}
                      {p.price_cents > 0 && <span className="text-xs font-normal text-slate-400">/{p.billing_interval === 'yearly' ? 'yr' : 'mo'}</span>}
                    </div>
                  </button>
                );
              })}
            </div>
          </Card>
          <div className="flex justify-end">
            <Button onClick={() => setStep(1)} disabled={!planKey}>
              Add-ons <ChevronRight size={16} />
            </Button>
          </div>
        </div>
      )}

      {/* ── STEP 2: ADD-ONS ──────────────────────────────────── */}
      {step === 1 && (
        <div className="space-y-4">
          <div className="grid gap-4 lg:grid-cols-[220px_1fr]">
            {/* category rail */}
            <Card className="p-2">
              <div className="flex flex-wrap gap-2 lg:flex-col">
                {cats.map(c => {
                  const meta = CAT_META[c.key] || CAT_META.other;
                  const Icon = meta.icon;
                  const active = activeCat === c.key;
                  const count = selList.filter(([k]) => addons.find(a => a.addon_key === k)?.category === c.key).length;
                  return (
                    <button key={c.key} onClick={() => setActiveCat(c.key)}
                      data-agent-id={`billing.addoncat.${c.key}`}
                      className={`flex shrink-0 items-center gap-2 rounded-lg px-3 py-2 text-left text-sm transition lg:w-full ${active ? 'bg-brand-500/10 font-medium text-brand-600 dark:text-brand-300' : 'text-slate-600 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-slate-800'}`}>
                      <Icon size={16} />
                      <span className="truncate">{meta.label}</span>
                      {count > 0 && <span className="ml-auto rounded-full bg-brand-500 px-1.5 text-[10px] font-bold text-white">{count}</span>}
                    </button>
                  );
                })}
              </div>
            </Card>

            <Card className="p-4">
              <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
                <div>
                  <h3 className="text-sm font-semibold text-slate-950 dark:text-slate-100">{(CAT_META[activeCat] || CAT_META.other).label}</h3>
                  <p className="text-xs text-slate-500">{(CAT_META[activeCat] || CAT_META.other).blurb}</p>
                </div>
                <div className="inline-flex rounded-lg bg-slate-100 p-1 dark:bg-slate-800">
                  {(['all', 'monthly', 'yearly'] as const).map(iv => (
                    <button key={iv} onClick={() => setAddonInterval(iv)}
                      className={`rounded-md px-2.5 py-1 text-xs font-medium capitalize ${addonInterval === iv ? 'bg-white text-slate-900 shadow-sm dark:bg-slate-700 dark:text-white' : 'text-slate-500'}`}>
                      {iv}
                    </button>
                  ))}
                </div>
              </div>

              <div className="space-y-2">
                {visibleAddons.length === 0 && <p className="text-sm text-slate-500">No add-ons in this category for this term.</p>}
                {visibleAddons.map(a => {
                  const sel = selection[a.addon_key];
                  const qty = sel?.quantity || 0;
                  return (
                    <div key={a.addon_key} className="rounded-lg border border-slate-200 p-3 dark:border-slate-700">
                      <div className="flex flex-wrap items-start justify-between gap-2">
                        <div className="min-w-0">
                          <div className="font-medium text-slate-950 dark:text-slate-100">{a.name}</div>
                          {a.unit_label && <div className="text-xs text-slate-500">{a.unit_label}</div>}
                        </div>
                        <div className="text-right">
                          <div className="font-semibold text-slate-950 dark:text-slate-100">{money(a.price_cents)}</div>
                          <div className="text-[11px] capitalize text-slate-400">{a.billing_interval || 'one-off'}</div>
                        </div>
                      </div>
                      {a.category === 'domain' && qty > 0 && (
                        <input placeholder="Desired domain name (e.g. mybrand.com)"
                          value={sel?.domain_name || ''}
                          onChange={e => setSelection(prev => ({ ...prev, [a.addon_key]: { ...(prev[a.addon_key] || { quantity: 1 }), domain_name: e.target.value } }))}
                          className="mt-2 w-full rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-sm dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100" />
                      )}
                      <div className="mt-2 flex items-center gap-2">
                        <button onClick={() => setQty(a.addon_key, qty - 1, a.max_quantity)}
                          disabled={qty <= 0} aria-label={`Remove one ${a.name}`}
                          className="rounded-md border border-slate-300 p-1.5 text-slate-600 disabled:opacity-40 dark:border-slate-600 dark:text-slate-300"><Minus size={14} /></button>
                        <span className="w-8 text-center text-sm font-medium tabular-nums">{qty}</span>
                        <button onClick={() => setQty(a.addon_key, qty + 1, a.max_quantity)}
                          aria-label={`Add one ${a.name}`}
                          className="rounded-md border border-slate-300 p-1.5 text-slate-600 dark:border-slate-600 dark:text-slate-300"><Plus size={14} /></button>
                        {qty > 0 && <span className="ml-1 text-xs font-medium text-brand-600 dark:text-brand-300">{money(a.price_cents * qty)}</span>}
                      </div>
                    </div>
                  );
                })}
              </div>
            </Card>
          </div>
          <div className="flex justify-between">
            <Button variant="secondary" onClick={() => setStep(0)}><ChevronLeft size={16} />Plan</Button>
            <div className="flex gap-2">
              <Button variant="secondary" onClick={addSelectionToCart} disabled={selList.length === 0}>
                <Plus size={16} />Add to cart
              </Button>
              <Button onClick={() => setStep(2)}>Review <ChevronRight size={16} /></Button>
            </div>
          </div>
        </div>
      )}

      {/* ── STEP 3: REVIEW ───────────────────────────────────── */}
      {step === 2 && (
        <div className="space-y-4">
          <Card className="p-5">
            <div className="mb-3 flex items-center gap-2">
              <Sparkles size={18} className="text-slate-500" />
              <h2 className="text-base font-semibold text-slate-950 dark:text-slate-100">Review your package</h2>
            </div>
            <div className="divide-y divide-slate-100 dark:divide-slate-800">
              <div className="flex items-center justify-between py-2">
                <span className="text-sm text-slate-700 dark:text-slate-300">
                  Plan: <strong>{selectedPlan?.display_name || '—'}</strong>
                  <span className="ml-1 capitalize text-slate-400">({interval})</span>
                </span>
                <span className="text-sm font-medium">{selectedPlan && selectedPlan.price_cents > 0 ? money(selectedPlan.price_cents) : (selectedPlan ? 'Free' : '—')}</span>
              </div>
              {selList.map(([k, v]) => {
                const a = addons.find(x => x.addon_key === k);
                if (!a) return null;
                return (
                  <div key={k} className="flex items-center justify-between py-2">
                    <span className="text-sm text-slate-700 dark:text-slate-300">
                      {a.name} <span className="text-slate-400">× {v.quantity}</span>
                      {v.domain_name && <span className="ml-1 text-xs text-slate-400">({v.domain_name})</span>}
                    </span>
                    <span className="text-sm font-medium">{money(a.price_cents * v.quantity)}</span>
                  </div>
                );
              })}
              {selList.length === 0 && <p className="py-2 text-sm text-slate-500">No add-ons selected.</p>}
            </div>
            <div className="mt-3 flex items-center justify-between border-t border-slate-200 pt-3 dark:border-slate-700">
              <span className="text-base font-semibold text-slate-950 dark:text-slate-100">Total</span>
              <span className="text-xl font-bold text-slate-950 dark:text-slate-100">{money(total)}</span>
            </div>
            <p className="mt-2 text-xs text-slate-400">
              You pay securely at checkout. Domain registrations are provisioned after payment clears.
            </p>
          </Card>

          {order && (
            <Card className="p-5">
              <div className="flex items-center gap-2 text-sm">
                <CheckCircle2 size={16} className="text-green-500" />
                <span className="text-slate-700 dark:text-slate-300">
                  Order <code className="rounded bg-slate-100 px-1.5 py-0.5 text-xs dark:bg-slate-800">{order.id.slice(0, 8)}</code> created
                  — status <strong className="capitalize">{order.status}</strong>, total {money(order.total_cents)}.
                </span>
              </div>
            </Card>
          )}

          <div className="flex justify-between">
            <Button variant="secondary" onClick={() => setStep(1)}><ChevronLeft size={16} />Add-ons</Button>
            <Button onClick={placeOrder} disabled={placing || !planKey}>
              {placing ? <LoadingSpinner size="sm" /> : <CreditCard size={16} />}
              {placing ? 'Creating…' : order ? 'Re-create order' : 'Create order & continue'}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
